import { useEffect, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

/** How long each line stays before the next takes its place. */
const LINE_MS = 2200;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/** On the page for a screen reader, off it for everyone else. In px: MUI reads 1 as 100%. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** Each line one after another, in place, or each kept as the next comes in below it. */
export type ReadingLinePacing = 'cycle' | 'stack';

/** Work still going on, or work done and kept where it happened. */
export type ReadingLineTone = 'working' | 'done';

export interface ReadingLineProps {
  /** Said in turn while the wait lasts; the last one stays. */
  lines: string[];
  /** Defaults to cycling through them. */
  pacing?: ReadingLinePacing;
  /** Defaults to working. */
  tone?: ReadingLineTone;
}

const lineSx = {
  // Neutral 600 rather than text.secondary, which fails contrast here.
  color: 'custom.neutral.600',
  fontStyle: 'italic',
  '@keyframes readingIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  animation: 'readingIn .4s ease both',
  [REDUCED_MOTION]: { animation: 'none' },
} as const;

/** Dots that keep moving, so an unchanged line still says it is working. */
const workingDots = [0, 1, 2].map((dot) => (
  <Box
    key={dot}
    component='span'
    sx={{
      '@keyframes readingDot': {
        '0%, 60%, 100%': { opacity: 0.2 },
        '30%': { opacity: 1 },
      },
      animation: 'readingDot 1.2s ease-in-out infinite',
      animationDelay: `${dot * 0.2}s`,
      [REDUCED_MOTION]: { animation: 'none' },
    }}
  >
    .
  </Box>
));

/**
 * ReadingLine says what is happening while what they said is read: a line on
 * Keni's side rather than a bubble, so it reads as work going on rather than
 * something said, with dots that keep moving so an unchanged line still says
 * it is working. Cycling, its lines take each other's place and the first is
 * said once to a screen reader; stacking, each stays as the next comes in, and
 * each is said as it comes.
 */
export const ReadingLine = ({
  lines,
  pacing = 'cycle',
  tone = 'working',
}: ReadingLineProps) => {
  const [at, setAt] = useState(0);
  const isStacked = pacing === 'stack';

  useEffect(() => {
    if (isStacked || at >= lines.length - 1) return;
    const timer = setTimeout(() => setAt((i) => i + 1), LINE_MS);
    return () => clearTimeout(timer);
  }, [isStacked, at, lines.length]);

  // Done, each line is said once: those still coming in are announced as they
  // land, while typing elsewhere says the work is still going.
  if (tone === 'done') {
    return (
      <Stack spacing={0.5} aria-live='polite' sx={{ maxWidth: '82%' }}>
        {lines.map((line, i) => (
          <Typography key={i} variant='body1' sx={lineSx}>
            {line}
          </Typography>
        ))}
      </Stack>
    );
  }

  if (isStacked) {
    return (
      <Stack spacing={0.5} sx={{ maxWidth: '82%' }}>
        <Box sx={visuallyHidden} role='status'>
          {lines.at(-1)}
        </Box>
        {/* Keyed by place, so only a line just come in fades in. */}
        {lines.map((line, i) => (
          <Typography key={i} aria-hidden variant='body1' sx={lineSx}>
            {line}
            {i === lines.length - 1 && workingDots}
          </Typography>
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={{ maxWidth: '82%' }}>
      <Box sx={visuallyHidden} role='status'>
        {lines[0]}
      </Box>
      <Typography key={at} aria-hidden variant='body1' sx={lineSx}>
        {lines[Math.min(at, lines.length - 1)]}
        {workingDots}
      </Typography>
    </Box>
  );
};

ReadingLine.displayName = 'ReadingLine';
export default ReadingLine;
